import { useState } from 'react';
import { HostMetricsPanel } from './host-metrics-panel';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { subHours, subDays, format } from 'date-fns';
import { BarChart3, Clock } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

interface MetricsDashboardProps {
  subscriptionId: string;
}

type TimeRange = {
  label: string;
  value: string;
  hours?: number;
  days?: number;
};

const TIME_RANGES: TimeRange[] = [
  { label: '1 Hour', value: '1h', hours: 1 },
  { label: '6 Hours', value: '6h', hours: 6 },
  { label: '24 Hours', value: '24h', hours: 24 },
  { label: '7 Days', value: '7d', days: 7 },
  { label: '30 Days', value: '30d', days: 30 },
];

export function MetricsDashboard({ subscriptionId }: MetricsDashboardProps) {
  const [timeRange, setTimeRange] = useState<string>('24h');

  const getTimeRange = () => {
    const now = new Date();
    const range = TIME_RANGES.find(r => r.value === timeRange);

    if (!range) {
      return { start: subHours(now, 24).toISOString(), end: now.toISOString() };
    }

    let start: Date;
    if (range.hours) {
      start = subHours(now, range.hours);
    } else if (range.days) {
      start = subDays(now, range.days);
    } else {
      start = subHours(now, 24);
    }

    return {
      start: start.toISOString(),
      end: now.toISOString(),
    };
  };

  const { start, end } = getTimeRange();
  const selectedRange = TIME_RANGES.find(r => r.value === timeRange);

  // Calculate step based on time range
  const getStep = () => {
    const range = TIME_RANGES.find(r => r.value === timeRange);
    if (!range) return '15s';

    // Keep each range at ~100-300 data points per series. Finer steps over
    // long ranges force VictoriaMetrics to evaluate hundreds of points per
    // metric across several concurrent queries, which can overwhelm it.
    if (range.hours && range.hours <= 1) return '15s'; // ~240 pts
    if (range.hours && range.hours <= 6) return '1m'; // ~360 pts
    if (range.hours && range.hours <= 24) return '5m'; // ~288 pts
    if (range.days && range.days <= 7) return '30m'; // ~336 pts
    return '3h'; // 30d -> ~240 pts
  };

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <BarChart3 className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <h1 className="text-3xl font-bold tracking-tight">Metrics Dashboard</h1>
          </div>
          <p className="text-sm text-slate-500 dark:text-slate-400 ml-12">
            Real-time monitoring and analytics for subscription performance
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
            <Clock className="h-4 w-4" />
            <span>Time Range</span>
          </div>
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-[180px] bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
              <SelectValue placeholder="Select time range" />
            </SelectTrigger>
            <SelectContent>
              {TIME_RANGES.map((range) => (
                <SelectItem key={range.value} value={range.value}>
                  {range.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Time Range Badge */}
      {selectedRange && (
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-xs font-normal">
            {format(new Date(start), 'MMM d, HH:mm')} - {format(new Date(end), 'MMM d, HH:mm')}
          </Badge>
        </div>
      )}

      {/* Main Content Card */}
      <Card className="border-slate-200 dark:border-slate-800 shadow-sm">
        <CardContent className="p-6">
          <HostMetricsPanel
            subscriptionId={subscriptionId}
            start={start}
            end={end}
            step={getStep()}
          />
        </CardContent>
      </Card>
    </div>
  );
}
